/**
 * Verb widget entry point.
 *
 * One script tag:
 *   <script src="https://api.askverb.com/v1/verb.js" data-verb-site="site_live_x" defer></script>
 *
 * Public API on window.Verb:
 *   Verb.open({ prompt })          open the panel, optionally pre-seeded
 *   Verb.ask(prompt)               open the panel AND send that question
 *   Verb.identify(token)           hand us a session token minted by YOUR backend
 *   Verb.reset()                   sign out: forget the conversation and the identity
 *   Verb.configure({ execute })    run tools with your own authenticated client
 */
import {
  anonRef,
  clearChat,
  forgetLocalHistory,
  forgetSessionToken,
  loadChat,
  persistSessionToken,
  saveChat,
  storedSessionToken,
  tokenSubject,
} from "./storage.js";
import type { ClientToolConfig } from "@verb/shared";
import {
  Panel,
  type DebugInfo,
  type DisplayMode,
  type DisplayRow,
  type Glow,
  type LauncherPosition,
  type Starter,
} from "./panel.js";
import { executeTool, toolFromExec, type AdapterFn } from "./execute.js";
import type { InputFieldSpec } from "./formFields.js";
import { detectCards } from "./cards.js";
import { clear as clearFiles } from "./files.js";
import { followLauncherTarget } from "./launcherTarget.js";
import { SseDroppedError, SseHttpError, streamSse } from "./sseClient.js";
import { shouldAskHost, transportMessage, visitorMessage } from "./identity.js";
import { turnstileToken as turnstileFor } from "./turnstile.js";

interface VerbApi {
  open(opts?: { prompt?: string }): void;
  /** Open and SEND. For a host page with its own "ask" box. See panel.ask. */
  ask(prompt: string): void;
  close(): void;
  identify(token: string): void;
  /**
   * Sign-out. Forgets the conversation, the saved transcript and the cached
   * identity, so the next person at this machine starts from nothing.
   *
   * Call it wherever the host app signs somebody out. `identify()` with a
   * different user clears on its own, so this is belt and braces for the case
   * where there is no next user to trigger that.
   */
  reset(): void;
  /**
   * `execute` runs tools in the host page's own session.
   *
   * `getSessionToken` is how the widget asks for a FRESH identity token, and
   * a site that identifies its users should supply it. Tokens are short-lived
   * on purpose, so a page open longer than the token's life has a stale one;
   * without this the widget can only discover that by having a question
   * refused. With it, the visitor never finds out.
   */
  configure(opts: { execute?: AdapterFn; getSessionToken?: SessionTokenFn }): void;
  readonly version: string;
}

declare global {
  interface Window {
    Verb?: VerbApi;
  }
}

/**
 * The tag that loaded us, and where every data-verb-* setting is read from.
 *
 * 🔴 `document.currentScript` IS NULL FOR A MODULE SCRIPT. That is the spec,
 *    not a quirk, and it means `<script type="module" data-verb-site="...">`
 *    read an empty site key and threw "Missing data-verb-site" before painting
 *    anything. The widget was simply absent, with one line in a console nobody
 *    had open.
 *
 *    It hid because the shipped bundle is an IIFE loaded as a classic script,
 *    where currentScript works, so production was always fine. The only page
 *    that loads the entry as a module is test/customer-site.html, which is the
 *    one place the widget gets exercised on a page that is not ours, so the
 *    harness for catching this class of bug was the thing the bug broke.
 *
 *    The fallback is not just for the dev page: a customer who adds
 *    `type="module"` to their tag, or loads us through a bundler, hits exactly
 *    this and sees nothing. Finding the tag by its own attribute is how every
 *    embedded script that supports async loading does it.
 */
const script =
  (document.currentScript as HTMLScriptElement | null) ??
  document.querySelector<HTMLScriptElement>("script[data-verb-site]");
const SITE_KEY = script?.dataset["verbSite"] ?? "";
const API_BASE =
  script?.dataset["verbApi"] ??
  (import.meta.env?.["VITE_VERB_API_BASE"] as string | undefined) ??
  "https://api.askverb.com";
const BRAND = script?.dataset["verbName"] ?? "Ask AI";
/** Optional picture for the assistant. Without it the avatar is its initial. */
const AVATAR = script?.dataset["verbAvatar"] ?? "";
/**
 * Pushing the site's own content aside is the default now. The one way out,
 * for the site whose layout does not tolerate a margin change, is this
 * attribute: `data-verb-layout="overlay"` returns to the panel simply
 * covering whatever is under it, the way it always used to.
 */
const PUSH_LAYOUT = script?.dataset["verbLayout"] !== "overlay";

/**
 * Complete control over placement, colour and shape, without waiting on the
 * dashboard's config call: these apply the instant the widget boots, and the
 * dashboard's own settings (below, once /v1/config resolves) can still
 * override them for a site that would rather configure this visually than
 * touch the script tag. Whichever sets a value LAST wins; the script tag
 * simply goes first, so there is never a flash of the wrong shape.
 */
const POSITION = asPosition(script?.dataset["verbPosition"]);
const DISPLAY_MODE = asDisplayMode(script?.dataset["verbMode"]);
const BRAND_COLOR = script?.dataset["verbColor"] ?? "";
const SHOW_LAUNCHER = script?.dataset["verbHideLauncher"] !== "true";
/**
 * 🔴 THESE TWO WERE MISSING AND THE FLASH THIS BLOCK PROMISES TO PREVENT WAS
 *    REAL. Glow and background could only come from /v1/config, which resolves
 *    after the launcher is on screen, so a site with either one set showed the
 *    plain button first and the configured one a beat later. It was
 *    photographed on askverb.com, both halves, before anybody thought to look
 *    at why the other five levers exist.
 */
const BACKGROUND_COLOR = script?.dataset["verbBg"] ?? "";
/**
 * A CSS selector for an element of the customer's own to put the button in,
 * instead of floating it in a corner. Script tag only: it names something in
 * their markup, which is a fact about their page rather than a preference, and
 * nothing in a dashboard can know it.
 */
const LAUNCHER_INTO = script?.dataset["verbLauncher"] ?? "";

/** Pixels from the corner, for a corner shared with something else. Script tag only. */
const OFFSET_X = asPixels(script?.dataset["verbOffsetX"]);
const OFFSET_Y = asPixels(script?.dataset["verbOffsetY"]);

function asPixels(v: string | undefined): number | undefined {
  const n = Number(v);
  // Bounded, so a typo cannot put the button off the screen.
  return v !== undefined && v !== "" && Number.isFinite(n) ? Math.min(Math.max(n, 0), 400) : undefined;
}
const GLOW = asGlow(script?.dataset["verbGlow"]);

function asGlow(v: string | undefined): Glow | undefined {
  return v === "off" || v === "brand" || v === "rainbow" ? v : undefined;
}

function asPosition(v: string | undefined): LauncherPosition | undefined {
  return v === "bottom-left" || v === "top-right" || v === "top-left" || v === "bottom-right"
    ? v
    : undefined;
}

function asDisplayMode(v: string | undefined): DisplayMode | undefined {
  return v === "dialog" || v === "panel" ? v : undefined;
}

interface BootConfig {
  assistantName: string;
  greeting: string;
  starters: Starter[];
  environment: "live" | "test";
  dryRun: boolean;
  requiresIdentity: boolean;
  originStatus: "verified" | "seen" | "pending" | "failed" | "unknown";
  /** Set on the dashboard, not the script tag: a site owner who would rather
   *  configure this visually than touch code. Undefined means "unset", not
   *  "off" or a default colour, so a script-tag hint is left alone. */
  brandColor?: string;
  avatarUrl?: string;
  /** A blob to wear instead of avatarUrl (migration 0067), by its seed. */
  avatarBlob?: string | null;
  /** Wear the avatar on the launcher instead of our mark. Absent on every site that did not ask. */
  launcherAvatar?: boolean;
  /** That face's size, a multiple of 52px: 1.25, 1.5, 1.75 or 2 (migration 0068). */
  launcherSize?: number;
  launcherTeaser?: string | null;
  launcherPosition?: LauncherPosition;
  displayMode?: DisplayMode;
  /** Push the page or cover it, set on the Theme tab. Absent: the script tag decides. */
  layout?: "push" | "overlay";
  showLauncher?: boolean;
  /** False only when the plan allows removing "Powered by Verb" and the owner did. */
  showBranding?: boolean;
  /** Set only when this site asks anonymous visitors for a Turnstile token. */
  turnstileSiteKey?: string | null;
  /** The widget's own ground colour, as opposed to brandColor's buttons. */
  backgroundColor?: string;
  glow?: Glow;
  /** The two chat bubbles. Absent keeps the derived shades. */
  bubbleUserColor?: string;
  bubbleAssistantColor?: string;
  /** What to say after somebody rates an answer helpful. docs/29 section 8.3. */
  feedbackThanks?: string | null;
  feedbackThanksUrl?: string | null;
}

/**
 * Today, in the visitor's own timezone, as YYYY-MM-DD.
 *
 * 🔴 BUILT FROM THE LOCAL PARTS, NOT toISOString(). toISOString converts to UTC
 *    first, which is exactly the bug this exists to avoid: it would hand back
 *    yesterday's date for anyone east of Greenwich in their early morning.
 */
function localDate(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Returns a freshly minted session token, or null if nobody is signed in. */
export type SessionTokenFn = () => Promise<string | null> | string | null;

const state = {
  // Restored eagerly, before mount, so a reload of an already-identified tab
  // never renders the anonymous state even for a frame. See storage.ts for
  // why this is safe to cache: it is the customer's own signed credential,
  // scoped to this tab, and never outlives what their backend already set.
  sessionToken: storedSessionToken(SITE_KEY) ?? undefined,
  adapter: null as AdapterFn | null,
  /** From /v1/config. Null, and so nothing happens, on every site but one that opted in. */
  turnstileSiteKey: null as string | null,
  /** Supplied by the host page through configure(). See refreshIdentity. */
  getSessionToken: null as SessionTokenFn | null,
  tools: [] as ClientToolConfig[],
  turnId: "",
  /** Whether the turn in flight ran any tool, which changes what a lost answer should warn about. */
  turnRanTool: false,
  /** Tools executing in this page right now. Leaving mid-action asks first. */
  toolsRunning: 0,
  // Restored with the transcript below, so the SERVER resumes the same
  // conversation rather than starting a fresh one behind an identical-looking
  // screen. Without it a reload put the history back on screen while the
  // assistant had forgotten all of it, which is worse than an empty panel.
  conversationId: loadChat(SITE_KEY)?.conversationId,
  busy: false,
  /**
   * The turn currently in flight, so New chat can actually stop it.
   *
   * 🔴 CLEARING THE SCREEN WITHOUT CUTTING THE CONNECTION IS THE WORST BUG OF
   *    THE THREE POSSIBLE ONES. The stream keeps arriving into a transcript
   *    that has been emptied, so the answer to a question nobody can see any
   *    more types itself back out, one delta at a time, under the greeting of
   *    a brand new conversation.
   */
  abort: null as AbortController | null,
  requiresIdentity: false,
  /** This origin is not verified, so every turn will be blocked by the browser. */
  blocked: false,
  /** The site's owner switched the assistant off (docs/65): it is not on the page. */
  off: false,
  /** Whether the site's settings have been applied yet; not when it booted switched off. */
  configured: false,
};

/**
 * 🔴 THE OFF SWITCH, FROM THE VISITOR'S SIDE (docs/65). The owner switches the assistant off in the
 *    dashboard and it leaves this page, in a tab that is already open, within about 30 seconds and
 *    without a reload: the panel closes, an answer being written is cut off, the button goes. Back
 *    on, it comes back the same way, with the conversation where it was.
 *
 *    Asked only while the tab is visible, and straight away when it becomes visible again, so a
 *    background tab costs nothing and a visitor returning to it never sees a stale assistant.
 */
const SWITCH_POLL_MS = 30_000;

function goOff(panel: Panel): void {
  if (state.off) return;
  state.off = true;
  state.abort?.abort();
  panel.setHidden(true);
}

function goOn(panel: Panel): void {
  if (!state.off) return;
  state.off = false;
  if (!state.configured) {
    // Booted switched off, so nothing was set up yet: do it now, as a fresh load would. Shown first,
    // because loadConfig only ever hides (a site that needs a signed-in user hides itself again there).
    panel.setHidden(false);
    void loadConfig(panel);
    return;
  }
  // Hidden again only for the reason it was hidden before: a site that needs a signed-in user.
  panel.setHidden(state.requiresIdentity && !state.sessionToken);
}

async function checkSwitch(panel: Panel): Promise<void> {
  try {
    const res = await fetch(`${API_BASE}/v1/config/state?key=${encodeURIComponent(SITE_KEY)}`, { credentials: "omit" });
    // Gone altogether (archived, or a wrong key): the same as off, and it stays gone.
    if (res.status === 404) return goOff(panel);
    if (!res.ok) return;
    const s = (await res.json()) as { on?: unknown };
    if (s.on === false) goOff(panel);
    else if (s.on === true) goOn(panel);
  } catch {
    // Offline or blocked: keep whatever we last knew. A network blip must not hide the assistant.
  }
}

function watchSwitch(panel: Panel): void {
  setInterval(() => {
    if (document.visibilityState === "visible") void checkSwitch(panel);
  }, SWITCH_POLL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkSwitch(panel);
  });
}

/**
 * The transcript as it currently stands, into sessionStorage.
 *
 * Two callers, and they are not the same event: the end of a turn, and a
 * starter answered from the site's own written text without a turn happening
 * at all. Both put words on screen that must survive a reload.
 */
function persist(panel: Panel): void {
  saveChat(SITE_KEY, {
    ...(state.conversationId ? { conversationId: state.conversationId } : {}),
    entries: panel.snapshot(),
  });
}

/**
 * Who this widget is, for every request that is about a turn already running.
 *
 * 🔴 EVERY FOLLOW-UP CARRIES IT. A turn id is in the URL of confirm, tool-result,
 *    input and outcome, and URLs get logged: a customer who fronts the widget with
 *    their own proxy has every one in their access logs. Alone it used to be
 *    enough to answer "Confirm" on somebody else's action or to read their saved
 *    answer. The server now remembers who started the turn and refuses anyone who
 *    cannot name the same visitor, so this goes in the BODY, never the URL.
 *    docs/56, findings F1 and F2.
 */
function visitorProof(): { anon_ref: string; session_token?: string } {
  return { anon_ref: anonRef(), ...(state.sessionToken ? { session_token: state.sessionToken } : {}) };
}

/** What to tell somebody whose domain is not verified yet, per exact state. */
function needsVerifying(status: string): string {
  const here = location.origin;
  if (status === "seen") {
    return `This assistant is not switched on for ${here} yet. We can see it here, so the last step is one click: open your Verb dashboard, find this site, and confirm the domain.`;
  }
  if (status === "failed") {
    return `Verification failed for ${here}. Open your Verb dashboard and check the domain on this site.`;
  }
  return `This assistant is not switched on for ${here} yet. Add and confirm this domain in your Verb dashboard and it will start working here.`;
}

/**
 * Fetches the site's display config, and records that we booted here.
 *
 * Failures are swallowed on purpose. The widget with default wording is a great
 * deal better than no widget, and the customer's page must not carry a console
 * error for a request that only affects our own greeting.
 */
async function loadConfig(panel: Panel): Promise<void> {
  try {
    const res = await fetch(`${API_BASE}/v1/config?key=${encodeURIComponent(SITE_KEY)}`, {
      credentials: "omit",
    });

    /**
     * 🔴 404 MEANS THE KEY NAMES NO ACTIVE SITE, NEVER AND NOT ANY MORE. A
     *    site is deleted from underneath a script tag that is still out
     *    there (the whole point of a soft delete, see the orchestrator's
     *    `archive` route), or the key was simply wrong. Either way there is
     *    nothing a visitor can do about it, so the fix is no launcher rather
     *    than one that opens into "Unknown site key" the moment they try to
     *    use it. Any OTHER failure (500, offline, blocked) falls through to
     *    the swallow below and keeps the widget with its defaults, this is
     *    the one status that means the site itself is gone.
     */
    if (res.status === 404) {
      panel.setHidden(true);
      return;
    }
    if (!res.ok) return;

    const cfg = (await res.json()) as Partial<BootConfig> & { off?: boolean };

    // Switched off by its owner (docs/65): not on this page until watchSwitch hears it is back on.
    if (cfg.off === true) {
      state.off = true;
      panel.setHidden(true);
      return;
    }
    state.configured = true;

    if (cfg.requiresIdentity === true) {
      state.requiresIdentity = true;
      // Hidden until Verb.identify() supplies a token. Stays visible if their
      // page already called it before this resolved, or if state restored a
      // still-valid token from an earlier identify() on this tab.
      if (!state.sessionToken) panel.setHidden(true);
    }

    if (typeof cfg.assistantName === "string") panel.setAssistantName(cfg.assistantName);

    /**
     * 🔴 BEFORE THE RESTORE BELOW, NOT AFTER. A restored transcript can contain
     *    a thumbs-up this visitor already gave, and settling it renders this
     *    line. Setting it afterwards would show the built-in thank-you on
     *    exactly the rows that came back from storage and the owner's on every
     *    row given afterwards, in the same panel.
     */
    panel.setFeedbackThanks(cfg.feedbackThanks, cfg.feedbackThanksUrl);

    /**
     * 🔴 THE GREETING IS FOR AN EMPTY PANEL, NOT FOR A RETURNING ONE. Showing
     *    "How can I help?" and a row of starter cards above a conversation
     *    already in progress reads as the assistant having forgotten it, which
     *    is the exact impression this restore exists to prevent.
     */
    const greeting = typeof cfg.greeting === "string" ? cfg.greeting : "";
    const starters = Array.isArray(cfg.starters) ? cfg.starters : [];

    const saved = loadChat(SITE_KEY);
    if (saved && saved.entries.length > 0) {
      // 🔴 THE OPENING IS RECORDED EVEN THOUGH IT IS NOT DRAWN. New chat puts
      //    it back, and a visitor who reloaded mid-conversation and then
      //    started over would otherwise get a blank panel with no cards: the
      //    one screen where a written answer would have cost nothing.
      panel.rememberOpening(greeting, starters);
      panel.restore(saved.entries);
    } else {
      panel.showOpening(greeting, starters);
    }

    /**
     * A site owner who configures this on the dashboard rather than in code
     * gets the same result as a script-tag hint, applied after it, so it
     * wins if both are set. Each is independently optional: setting a brand
     * colour on the dashboard says nothing about where the launcher sits.
     */
    if (typeof cfg.avatarUrl === "string" && cfg.avatarUrl) panel.setAvatar(cfg.avatarUrl);
    if (typeof cfg.avatarBlob === "string" && /^[a-z0-9-]{1,40}$/.test(cfg.avatarBlob)) panel.setAvatarBlob(cfg.avatarBlob);
    // After setAvatar: the button can only wear a picture it has been given.
    if (cfg.launcherAvatar === true) panel.setLauncherAvatar(true);
    if (typeof cfg.launcherSize === "number") panel.setLauncherSize(cfg.launcherSize);
    if (cfg.launcherTeaser) panel.setLauncherTeaser(cfg.launcherTeaser);
    if (typeof cfg.brandColor === "string" && cfg.brandColor) panel.setBrandColor(cfg.brandColor);
    if (typeof cfg.backgroundColor === "string" && cfg.backgroundColor) {
      panel.setBackgroundColor(cfg.backgroundColor);
    }
    // Applied even when it is "off": a site that turned the glow back off has
    // to be able to overrule a script tag that asked for one, which is the
    // same reason showLauncher checks for the type rather than the value.
    if (cfg.glow === "off" || cfg.glow === "brand" || cfg.glow === "rainbow") {
      panel.setGlow(cfg.glow);
    }
    if (typeof cfg.bubbleUserColor === "string" && cfg.bubbleUserColor) {
      panel.setBubbleColor("user", cfg.bubbleUserColor);
    }
    if (typeof cfg.bubbleAssistantColor === "string" && cfg.bubbleAssistantColor) {
      panel.setBubbleColor("assistant", cfg.bubbleAssistantColor);
    }
    if (cfg.launcherPosition) panel.setPosition(cfg.launcherPosition);
    if (cfg.displayMode) panel.setDisplayMode(cfg.displayMode);
    if (cfg.layout) panel.setPushLayout(cfg.layout === "push");
    if (typeof cfg.showLauncher === "boolean") panel.setLauncherVisible(cfg.showLauncher);
    if (cfg.showBranding === false) panel.setShowBranding(false);
    // Null on every site that has not opted in, which loads no third-party script.
    state.turnstileSiteKey = typeof cfg.turnstileSiteKey === "string" ? cfg.turnstileSiteKey : null;

    /**
     * 🔴 SAY IT NOW, NOT AFTER THEY TYPE.
     *
     *    An unverified origin gets no CORS header on the turn, so the browser
     *    blocks it and the only thing we can observe is "Failed to fetch",
     *    identical to being offline. Waiting until they send a message means
     *    they compose a question, press send, and get a network error about
     *    a permissions problem.
     *
     *    Shown once at open, in their own panel, naming the exact next click.
     */
    if (cfg.originStatus && cfg.originStatus !== "verified") {
      state.blocked = true;
      panel.addNotice(needsVerifying(cfg.originStatus), "error");
    }
  } catch {
    // Offline, blocked by a content blocker, or a network hiccup. Defaults hold.
  } finally {
    /**
     * 🔴 IN THE finally, SO A FAILED CONFIG STILL SHOWS THE BUTTON. The
     *    launcher starts hidden because its colours are set in the dashboard
     *    and arrive here rather than from the script tag, and painting Verb's
     *    own gold first meant every visitor to a site with a custom colour
     *    watched a yellow button repaint into a different one.
     *
     *    Revealed once this call has SETTLED, not once it succeeded. A site
     *    whose config 404s or is eaten by an extension keeps the defaults,
     *    which is the existing behaviour, and it must still get a button.
     */
    panel.markReady();
  }
}

/**
 * Puts the widget's typeface in front of the browser.
 *
 * 🔴 IT HAS TO GO IN THE HOST DOCUMENT, AND THAT IS NOT A CHOICE. Font faces
 *    are document-scoped: an @font-face rule inside a shadow root is ignored
 *    by every engine, so the panel would silently render in the fallback stack
 *    no matter what the shadow stylesheet said. One style element, appended
 *    once, is the only way.
 *
 * 🔴 THE FAMILY IS NAMED "Verb Geist", NOT "Geist". A customer who already
 *    uses Geist has their own @font-face for it, quite possibly a different
 *    subset, weight range or version. Registering a second face under the same
 *    name would have the two fight over one family in their document, and the
 *    loser would be whichever loaded second, on their page, for their users.
 *    A private name cannot collide with anything.
 *
 * 🔴 AND IT NEVER BLOCKS THEIR PAGE. font-display: swap, so text paints
 *    immediately in the system fallback and upgrades when the font lands. A
 *    widget that delayed somebody else's first paint for a typeface would
 *    deserve to be removed.
 */
function installFontFace(): void {
  const ID = "verb-font-face";
  if (document.getElementById(ID)) return;

  const style = document.createElement("style");
  style.id = ID;
  style.textContent =
    "@font-face{font-family:'Verb Geist';font-style:normal;font-weight:400 700;" +
    "font-display:swap;src:url('" +
    API_BASE +
    "/v1/geist-latin.woff2') format('woff2');}";
  document.head.appendChild(style);
}

function boot(): void {
  if (!SITE_KEY) throw new Error("Missing data-verb-site on the Verb script tag");

  installFontFace();

  const panel = new Panel(
    {
      onSend: (message) => void sendTurn(panel, message),
      onEdit: (message, rewindTurns, replaces) =>
        void sendTurn(panel, message, rewindTurns, replaces),
      onConfirm: (callId, decision, values) => {
        // The turn resumes the moment they answer, so say so straight away.
        if (decision === "allow") panel.showThinking("Running that now");
        void postConfirm(panel, callId, decision, values);
      },
      onInput: (callId, values) => {
        if (values) panel.showThinking("Running that now");
        void postInput(panel, callId, values);
      },
      onOpen: () => {},
      onFeedback: (messageId, rating, comment) => void sendFeedback(messageId, rating, comment),
      // No turn ran, so nothing else is going to write this down.
      onAnswered: () => persist(panel),
      /**
       * 🔴 THE CONVERSATION ID IS THE POINT. The panel has already cleared the
       *    screen; what only this can do is stop the NEXT question resuming
       *    the thread the user just closed. Dropping it here means the
       *    orchestrator opens a fresh conversation, so the model starts from
       *    nothing, which is what "new chat" says on the button.
       */
      onNewChat: () => {
        // Cuts the connection first: see state.abort for what happens if the
        // screen is cleared while the answer is still arriving.
        state.abort?.abort();
        state.abort = null;
        state.conversationId = undefined;
        state.turnId = "";
        state.busy = false;
        /**
         * 🔴 A FILE ATTACHED TO THE CONVERSATION THAT JUST ENDED HAS NO
         *    BUSINESS SURVIVING IT. Left uncleared, its preview object URL
         *    (a real memory hold) and the File itself sat in this map for as
         *    long as the tab stayed open, through any number of New Chats,
         *    because nothing ever called files.ts's own clear(). Found
         *    2026-09-22, answering "should New Chat be a genuinely fresh
         *    start": for everything this widget owns, yes.
         */
        clearFiles();
        /**
         * 🔴 NOT LEFT RUNNING. A tool executing at the exact moment New Chat
         *    is pressed has no abort wired into its own fetch (cancelling a
         *    write mid-flight risks the request having already reached the
         *    customer's server, which is worse than letting it finish
         *    unseen), so the count is simply reset here. Left alone, it could
         *    stay above zero forever and the "Leave site?" prompt would keep
         *    guarding an action the person already walked away from.
         */
        state.toolsRunning = 0;
        clearChat(SITE_KEY);
      },
    },
    BRAND,
    {
      pushLayout: PUSH_LAYOUT,
      ...(POSITION ? { position: POSITION } : {}),
      ...(DISPLAY_MODE ? { displayMode: DISPLAY_MODE } : {}),
      showLauncher: SHOW_LAUNCHER,
      ...(BRAND_COLOR ? { brandColor: BRAND_COLOR } : {}),
      ...(BACKGROUND_COLOR ? { backgroundColor: BACKGROUND_COLOR } : {}),
      ...(GLOW ? { glow: GLOW } : {}),
    },
  );
  if (AVATAR) panel.setAvatar(AVATAR);
  /**
   * The page took "Powered by Verb" away and the panel put it back: tell us,
   * once per page load. A beacon, so it needs no CORS answer and survives the
   * visitor leaving. The admin panel lists the sites (/v1/config/branding).
   */
  let brandingReported = false;
  panel.onBrandingRestored = () => {
    if (brandingReported) return;
    brandingReported = true;
    try {
      navigator.sendBeacon?.(`${API_BASE}/v1/config/branding?key=${encodeURIComponent(SITE_KEY)}`);
    } catch {
      // Reporting is a courtesy; the line is already back either way.
    }
  };
  panel.mount();
  /**
   * After mount, because the launcher is moved OUT of the main shadow root and
   * that root has to exist first. The script is `defer`, so the customer's own
   * markup is parsed by now; a selector that still matches nothing warns and
   * leaves the floating button where it is.
   */
  panel.setLauncherOffset(OFFSET_X, OFFSET_Y);
  if (LAUNCHER_INTO) followLauncherTarget(panel, LAUNCHER_INTO);

  /**
   * 🔴 THE BOOT CALL, WHICH NOTHING MADE UNTIL NOW.
   *
   *    `/v1/config` was written, tested and deployed, and the widget never
   *    called it. Three things were broken by that and none of them looked
   *    broken from anywhere:
   *
   *      1. The assistant's name, greeting and starter cards, all set in the
   *         dashboard, reached nothing at all.
   *      2. THE DOMAIN SIGHTING WAS NEVER RECORDED. Recording it is a side
   *         effect of this endpoint, so "we saw your widget boot on acme.com,
   *         is that yours?" could never happen and every customer would have
   *         had to verify by pasting a meta tag.
   *      3. There was nowhere for a site to say it is behind a login.
   *
   *    It runs after mount so a slow or failed config never delays the panel
   *    existing, and a failure leaves the widget working with its defaults.
   */
  void loadConfig(panel);
  watchSwitch(panel);

  const api: VerbApi = {
    open: (opts) => panel.open(opts?.prompt),
    ask: (prompt: string) => panel.ask(prompt),
    close: () => panel.close(),
    /**
     * The customer's backend mints a short-lived JWT and hands it here. We treat
     * it as OPAQUE, never inspect its contents for anything of our own, their
     * backend decides who this is and what they may do. Cached to sessionStorage
     * (storage.ts) so a reload does not throw the visitor back to anonymous
     * before this gets called again; the cache respects the token's own `exp`
     * and dies with the tab, so it never outlives what their backend decided.
     */
    identify: (token: string) => adoptIdentity(panel, token || null),
    /**
     * Sign-out, for the host application to call.
     *
     * 🔴 THIS IS THE API VAKEEL CHAMBERS ASKED FOR, AND THEY WERE RIGHT THAT
     *    THERE WAS NO SUPPORTED WAY TO DO THIS. The public surface was
     *    configure, identify, open, close, ask, version. An integrator who
     *    noticed the leak could only reach into sessionStorage on their own
     *    origin and delete our keys by name, which works and is not something
     *    anybody should have to reverse engineer from our bundle.
     *
     *    `identify()` with a different user now clears on its own, so this is
     *    not the only defence. It is the one for signing out, where there is no
     *    next user to notice the change.
     */
    reset: () => {
      forgetEverythingLocal(panel);
      state.sessionToken = undefined;
      if (state.requiresIdentity) panel.setHidden(true);
    },
    configure: (opts) => {
      if (opts.execute) state.adapter = opts.execute;
      if (opts.getSessionToken) state.getSessionToken = opts.getSessionToken;
    },
    version: "0.0.1",
  };
  window.Verb = api;

  /**
   * 🔴 THE TAG IS `defer`, SO "AFTER THE SCRIPT TAG" IS NOT "AFTER IT LOADED".
   *    Code placed BELOW the tag in the document still runs before it, so
   *    `window.Verb.configure(...)` throws on undefined, and both the adapter
   *    and identity instructions said "after the script tag" with no way to
   *    wait offered anywhere. The cal.com integration got past it with
   *    next/script's onLoad; a plain-HTML integrator had nothing.
   *
   *    Anyone who missed the event can still test for `window.Verb` directly,
   *    which is why the generated snippet checks both. Fired on `window`
   *    rather than `document` so the snippet has one obvious place to listen.
   */
  window.dispatchEvent(new Event("verb:ready"));

  // Customers can drive the panel from their own markup.
  document.addEventListener("click", (e) => {
    const target = (e.target as Element | null)?.closest?.("[data-verb-open]");
    if (target) {
      e.preventDefault();
      panel.open(target.getAttribute("data-verb-open") || undefined);
    }
  });
}

/**
 * Takes on a new identity, and drops the previous person's conversation when
 * the person actually changed.
 *
 * 🔴 REPORTED BY VAKEEL CHAMBERS, 2026-09-04, AND IT IS A CONFIDENTIALITY BUG
 *    RATHER THAN A TIDINESS ONE. A clerk and the Head of Chambers share a
 *    machine. The admin asked about invoices, signed out, and the next person
 *    to sign in opened the panel and read the whole thing: client names and
 *    invoice figures, still on screen.
 *
 *    🔴 NOTHING ESCALATED, AND THAT PART IS WORTH BEING PRECISE ABOUT. Every
 *       tool call went through their API under the NEW user's own token and
 *       was refused exactly as it should have been. What leaked was the
 *       rendered transcript, which is `verb_chat_*` in sessionStorage on their
 *       own origin, and it leaked because `identify()` swapped the token and
 *       left the conversation sitting there.
 *
 *    🔴 AND THE FUNCTION THAT FIXES IT ALREADY EXISTED AND WAS NEVER CALLED.
 *       `forgetLocalHistory` in storage.ts is documented, word for word, as
 *       "called when the visitor signs out of the host application, so the
 *       next person at a shared machine does not resume their conversation".
 *       Nothing called it. `refreshIdentity` even names it in a comment while
 *       not calling it. Dead code that describes the exact leak it is not
 *       preventing is worse than no code: it reads as handled.
 *
 * 🔴 KEYED ON `sub`, NEVER ON THE TOKEN STRING. Tokens are short lived and
 *    `getSessionToken` fetches a fresh one every few minutes, so comparing the
 *    strings would clear the transcript mid-conversation, repeatedly, for one
 *    person sitting still. The subject is the only part that says WHO.
 *
 * 🔴 ANONYMOUS TO IDENTIFIED IS NOT A CHANGE. Somebody browsing, then signing
 *    in, is the same human continuing, and wiping what they just asked would
 *    be a bug of our own making. Only an identity that was there and is now
 *    different, or gone, clears.
 */
function adoptIdentity(panel: Panel, token: string | null): void {
  const before = tokenSubject(state.sessionToken);
  const after = tokenSubject(token);

  if (before && before !== after) {
    /**
     * 🔴 THE WIPE IS CORRECT AND IT USED TO BE SILENT, WHICH IS NOT.
     *
     *    A different `sub` means a different person, so the previous person's
     *    conversation must not stay on screen. That part is not negotiable.
     *    What was missing is any account of it: the panel simply emptied, and
     *    if a turn was in flight its stream was cut, which on the server
     *    resolves every waiting confirmation and reports the action as not
     *    carried out.
     *
     *    On 2026-09-08 that happened during a demo. Somebody signed in as a
     *    second user to show permissions, signed back, answered the question
     *    the assistant had asked, and watched it ask the identical question
     *    again with a dead input. Nothing on screen ever said an account
     *    change had discarded the turn. See docs/13, and DeniedReason in
     *    runTool.ts for the other half of this fix.
     *
     *    The notice is added AFTER the wipe, because the wipe empties the
     *    transcript it is being added to.
     */
    const interrupted = panel.isBusy();
    forgetEverythingLocal(panel);
    panel.addNotice(
      interrupted
        ? "You signed in as a different user, so I stopped what I was doing and cleared the conversation. Nothing was carried out. Ask again and I will do it as the new user."
        : "You signed in as a different user, so I cleared the previous conversation.",
      interrupted ? "error" : "info",
    );
  }

  if (token) {
    state.sessionToken = token;
    persistSessionToken(SITE_KEY, token);
    // A site that hid itself pending identity can now show itself. The token
    // usually arrives after boot, because fetching it is a round trip.
    if (state.requiresIdentity && !state.off) panel.setHidden(false);
    return;
  }

  state.sessionToken = undefined;
  forgetSessionToken(SITE_KEY);
  /**
   * 🔴 AND ON A SITE THAT REQUIRES IDENTITY, THE PANEL GOES WITH IT. Without
   *    this the launcher stayed on screen after a sign-out and every question
   *    was refused with "Your session ended", which is the exact state
   *    `requiresIdentity` exists to prevent: an assistant offered to somebody
   *    it will refuse.
   */
  if (state.requiresIdentity) panel.setHidden(true);
}

/**
 * Everything this tab knows about the person who was just here.
 *
 * The screen, the conversation id, the saved transcript, the anonymous ref and
 * the cached token. `forgetLocalHistory` deliberately clears EVERY site's
 * transcript rather than only this one, because a sign-out is a statement about
 * the person at the keyboard, not about one widget.
 */
function forgetEverythingLocal(panel: Panel): void {
  // newChat cuts any stream in flight, empties the screen and, through
  // onNewChat, drops the conversation id so the next question does not resume
  // the thread that just ended.
  panel.newChat();
  forgetLocalHistory();
  forgetSessionToken(SITE_KEY);
}

async function refreshIdentity(panel: Panel): Promise<string | null> {
  if (!state.getSessionToken) return null;
  try {
    const token = await state.getSessionToken();
    if (typeof token === "string" && token) {
      // Through adoptIdentity, not around it: this is the path a SHARED MACHINE
      // actually takes. The host page signs one person out and another in, and
      // the widget hears about it here, on the next turn, rather than through
      // an identify() call anybody remembered to make.
      adoptIdentity(panel, token);
      return token;
    }
    /**
     * 🔴 AN EXPLICIT null MEANS SIGNED OUT, AND THE OLD TOKEN MUST GO. Keeping
     *    it would send the previous person's identity on the next turn, on a
     *    shared machine, which is the exact thing forgetLocalHistory exists to
     *    prevent at the other end of the session.
     */
    adoptIdentity(panel, null);
    return null;
  } catch {
    // Their endpoint is down or the network blinked. Nothing to say to the
    // visitor: the turn below either works anonymously or reports its own
    // failure in its own words.
    return null;
  }
}

async function sendTurn(
  panel: Panel,
  message: string,
  rewindTurns = 0,
  replaces = "",
): Promise<void> {
  if (state.busy || state.off) return;
  /**
   * 🔴 OFFLINE AT SEND: SAY SO, AND KEEP THE WORDS. The request would fail in
   *    the same way a second later, after a spinner, and the question the
   *    visitor typed would be gone with it.
   */
  if (!navigator.onLine) {
    panel.returnDraft(message);
    panel.addNotice(transportMessage(false), "error");
    return;
  }
  state.busy = true;
  panel.setBusy(true);
  // Cleared, so a drop before `ready` cannot recover the PREVIOUS turn's answer.
  state.turnId = "";
  state.turnRanTool = false;
  const controller = new AbortController();
  state.abort = controller;
  // Before the request is even made. The slowest part of a turn is the wait for
  // the first token, and that is exactly the part that had nothing on screen.
  panel.showThinking("Thinking");

  /**
   * 🔴 REFRESHED BEFORE THE REQUEST, NOT ONLY AFTER IT FAILS. Recovering from
   *    the 401 below is necessary and not sufficient: without this the visitor
   *    still waits out a round trip that was always going to be refused, and
   *    every identified visitor pays that once every 15 minutes. Checking the
   *    token we already hold costs nothing and means they usually never learn
   *    the token existed.
   *
   * 🔴 AND IT ASKS WHEN THERE IS NO TOKEN AT ALL, NOT ONLY WHEN ONE IS EXPIRING.
   *    `isStale` alone left no way back from anonymous: see shouldAskHost in
   *    identity.ts for the bug that reached a real integration.
   */
  if (shouldAskHost(state.sessionToken, !!state.getSessionToken)) await refreshIdentity(panel);

  /**
   * 🔴 ONLY WHERE THE SITE ASKED FOR IT, AND ONLY WHEN NOBODY IS SIGNED IN. A
   *    signed-in visitor already proved who they are, and the server does not
   *    ask them either (routes/turn.ts). One token per turn: Cloudflare rejects
   *    a replayed one, which is what makes it worth asking for.
   *
   *    Null when Turnstile could not produce one, including a page whose
   *    content-security policy blocks the script. The turn is still sent and
   *    the server decides, which keeps that rule in one place rather than two.
   */
  const turnstileToken =
    state.turnstileSiteKey && !state.sessionToken
      ? await turnstileFor(state.turnstileSiteKey)
      : null;

  const send = (): Promise<void> =>
    streamSse({
      signal: controller.signal,
      url: `${API_BASE}/v1/turn`,
      body: {
        site_key: SITE_KEY,
        message,
        anon_ref: anonRef(),
        // Says this widget attaches proof to every follow-up, so the server may
        // refuse a follow-up that cannot show it. Absent, a turn keeps the old
        // unchecked behaviour, which is what an older cached widget gets.
        proto: 2,
        ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
        ...(state.sessionToken ? { session_token: state.sessionToken } : {}),
        ...(state.conversationId ? { conversation_id: state.conversationId } : {}),
        ...(rewindTurns > 0 ? { rewind_turns: rewindTurns, rewind_replaces: replaces } : {}),
        /**
         * 🔴 THE ASSISTANT HAD NO IDEA WHAT DAY IT WAS. The server knows only
         *    UTC, and "today" means today where the person asking is standing;
         *    India is UTC+5:30, so every night there is a five and a half hour
         *    window where those disagree. On a legal product that is a wrong
         *    filing date, so the date comes from the clock of the person who
         *    said "today".
         */
        client_date: localDate(),
      },
      onMessage: (msg) => handleEvent(panel, msg.event, msg.data),
    });

  let retriedWithFreshToken = false;

  try {
    try {
      await send();
    } catch (err) {
      /**
       * 🔴 ONE RETRY, AND ONLY FOR A 401 RAISED BEFORE ANYTHING STREAMED.
       *    SseHttpError is thrown from the status check, so by construction no
       *    part of the answer has been delivered and re-sending cannot
       *    duplicate anything or re-run a tool. A mid-stream failure is a
       *    different error type and is deliberately not retried: repeating a
       *    turn that already ran tools is the one thing this product must
       *    never do.
       *
       * 🔴 AND ONLY WHEN A NEW TOKEN ACTUALLY ARRIVED. Retrying with the same
       *    expired credential is a second identical refusal and twice the wait.
       */
      const expired = err instanceof SseHttpError && err.status === 401;
      if (!expired || controller.signal.aborted) throw err;

      /**
       * 🔴 THE DEAD END IS NAMED, IN THE CONSOLE, WHERE THE DEVELOPER IS.
       *    Both recovery paths, the proactive refresh above and this retry,
       *    go through refreshIdentity, which returns null immediately when the
       *    page never supplied getSessionToken. So a site that hands over a
       *    single token via identify() works perfectly for fifteen minutes and
       *    then tells a signed-in person to sign in, with nothing anywhere
       *    saying why.
       *
       *    Vakeel Chambers hit exactly this and had to ask. The visitor still
       *    sees the honest "your session ended"; this is for the one person who
       *    can fix it, and it names the call rather than the concept.
       */
      if (!state.getSessionToken) {
        console.warn(
          "[verb] The session token expired and no getSessionToken was configured, so it " +
            "could not be refreshed. Pass one: window.Verb.configure({ getSessionToken }). " +
            "See https://askverb.com/docs/identity",
        );
      }

      const fresh = await refreshIdentity(panel);
      if (!fresh) throw err;
      /**
       * 🔴 REMEMBERED, SO A SECOND 401 CAN BE CALLED WHAT IT IS. Getting here
       *    means the host just minted a brand new token. If that is refused as
       *    well, the credential is not expired, it is not accepted at all, and
       *    telling the visitor to refresh would loop them forever. See
       *    visitorMessage.
       */
      retriedWithFreshToken = true;
      await send();
    }
  } catch (err) {
    // 🔴 A CANCELLED TURN IS NOT A FAILED ONE. The user asked for this by
    //    starting a new chat, and "Could not reach the assistant" on top of
    //    the empty panel they just asked for would read as a bug they caused.
    if (!controller.signal.aborted) {
      // A blocked origin fails with the same TypeError as being offline, so the
      // reason we already know about wins over the one we are guessing at.
      /**
       * 🔴 THE VISITOR NEVER SEES THE SERVER'S OWN WORDS. This renders on a
       *    customer's website in front of THEIR users, and the server writes
       *    for whoever installed the widget. Rendering `err.message` put
       *    "Invalid session token" on askverb.com's dashboard in red, which
       *    told the person nothing and read like a security problem. The
       *    detail goes to the console, where the developer is.
       */
      if (err instanceof SseDroppedError) {
        await recoverTurn(panel, controller.signal);
        return;
      }
      if (err instanceof SseHttpError) {
        console.warn(`[verb] turn refused: ${err.status} ${err.detail}`);
        /**
         * 🔴 NAMES THE ONE CAUSE THAT LOOKS LIKE EVERY OTHER CAUSE. A fresh
         *    token refused immediately after minting is almost always a key
         *    that belongs to the OTHER environment: live and test are separate
         *    sites with separate signing keys, and the keys are identical in
         *    shape, so nothing about them says which is which. This cost the
         *    cal.com integration most of a session, with the only clue being a
         *    401 whose text named neither the cause nor the fix.
         */
        if (err.status === 401 && retriedWithFreshToken) {
          console.warn(
            "[verb] A freshly minted token was refused, so this is not an expiry. " +
              "Check that VERB_SIGNING_KEY belongs to the SAME site as your " +
              `data-verb-site key (${SITE_KEY}). Live and test are separate sites ` +
              "with separate signing keys. See https://askverb.com/docs/identity",
          );
        }
      }
      panel.addNotice(
        state.blocked
          ? needsVerifying("seen")
          : err instanceof SseHttpError
            ? err.status === 403
              ? needsVerifying("seen")
              : visitorMessage(err.status, retriedWithFreshToken)
            : transportMessage(),
        "error",
      );
      // Never connected, so the server does not have this question: give the
      // words back rather than leaving them in a bubble nobody answered.
      if (!(err instanceof SseHttpError)) panel.returnDraft(message);
    }
  } finally {
    if (state.abort === controller) state.abort = null;
    /**
     * 🔴 NOTHING TO TIDY IF IT WAS CANCELLED. newChat has already emptied the
     *    transcript, dropped the conversation id, cleared storage and unlocked
     *    the composer. Running the normal ending here would write the emptied
     *    transcript BACK into sessionStorage, over the clear that just
     *    happened, so a reload would restore a conversation of one greeting.
     */
    if (!controller.signal.aborted) {
      panel.endStream();
      panel.hideThinking();
      // Whatever happened, this turn is over: fold its tool chips into one
      // summary. In the finally rather than on the `saved` event, because a turn
      // that errored or was cut off still leaves chips on screen and they still
      // belong to a turn that has ended.
      panel.finishTurn();
      // Written once per turn, at the end, rather than on every token: a save
      // per delta would serialise the whole transcript hundreds of times during
      // one answer.
      persist(panel);
      state.busy = false;
      panel.setBusy(false);
    }
  }
}

function handleEvent(panel: Panel, event: string, data: unknown): void {
  const d = (data ?? {}) as Record<string, unknown>;

  switch (event) {
    // The site was switched off since this page loaded (docs/65): leave now, not on the next poll.
    case "off":
      goOff(panel);
      break;

    case "mode":
      // Arrives before anything else. Labels the panel for the whole session.
      panel.setMode(
        d["environment"] === "test" ? "test" : "live",
        d["dryRun"] === true,
      );
      break;

    case "ready":
      state.turnId = String(d["turn_id"] ?? "");
      state.conversationId = String(d["conversation_id"] ?? "") || undefined;
      // Absent on a bound turn: the details of an action arrive WITH its call.
      state.tools = (d["tools"] as ClientToolConfig[] | undefined) ?? [];
      break;

    case "text":
      panel.appendDelta(String(d["delta"] ?? ""));
      break;

    case "tool_start":
      state.turnRanTool = true;
      panel.endStream();
      // `label` is the customer's own sentence; `name` is sent only to a widget that
      // predates labels, and an action's name is not something a visitor should read.
      panel.addToolChip(
        String(d["id"]),
        d["name"] == null ? "" : String(d["name"]),
        typeof d["label"] === "string" ? d["label"] : undefined,
      );
      break;

    case "tool_call":
      void runToolCall(
        panel,
        String(d["id"]),
        String(d["name"]),
        (d["args"] as Record<string, unknown>) ?? {},
        d["exec"],
      );
      break;

    case "tool_unconfirmed":
      panel.markToolUnconfirmed(String(d["id"]));
      break;

    case "tool_stale":
      panel.markToolChanged(String(d["id"]));
      break;

    case "tool_end":
      panel.updateToolChip(String(d["id"]), String(d["status"] ?? "ok"));
      // The tool is done and the model has to read the result before it says
      // anything. Another silent gap, so another label rather than nothing.
      panel.showThinking("Reading the result");
      break;

    /**
     * The turn is on disk. Only now may this question be counted as a turn an
     * edit can unwind: unwinding one that was never written would delete a real
     * exchange that came before it.
     */
    case "saved":
      panel.commitTurn();
      // The answer is on disk and has an id, so it can now be rated. Offered
      // here and nowhere else: a turn that failed or was cut off never reaches
      // this event, and rating an answer the server did not keep would post an
      // id that resolves to nothing.
      panel.offerFeedback(String(d["message_id"] ?? ""));
      break;

    case "confirm_request":
      panel.endStream();
      // Waiting on a person now, not on us. A pulsing "thinking" beside a
      // question we are not answering is a lie about who is holding things up.
      panel.hideThinking();
      panel.addConfirm(
        String(d["id"]),
        String(d["label"] ?? d["name"] ?? "Confirm action"),
        (d["args"] as Record<string, unknown>) ?? {},
        d["warning"] ? String(d["warning"]) : undefined,
        d["fields"] as InputFieldSpec[] | undefined,
        Array.isArray(d["display"]) ? (d["display"] as DisplayRow[]) : undefined,
        d["debug"] && typeof d["debug"] === "object" ? (d["debug"] as DebugInfo) : undefined,
      );
      break;

    case "input_request":
      panel.endStream();
      // Same reasoning as the confirmation above: we are waiting on a person
      // now, and a pulsing "thinking" beside a question we are not answering is
      // a lie about who is holding things up.
      panel.hideThinking();
      panel.addInputRequest(
        String(d["id"]),
        String(d["label"] ?? d["name"] ?? "More information needed"),
        (d["fields"] as InputFieldSpec[] | undefined) ?? [],
      );
      break;

    case "unavailable":
      panel.endStream();
      panel.hideThinking();
      // A calm sentence. The site owner sees the real reason in their dashboard;
      // their visitor must never learn this site hit its AI budget.
      panel.addNotice(String(d["message"] ?? "The assistant is temporarily unavailable."));
      break;

    case "error":
      panel.endStream();
      panel.hideThinking();
      panel.addNotice(String(d["message"] ?? "Something went wrong."), "error");
      break;

    case "done":
      panel.endStream();
      panel.hideThinking();
      break;
  }
}

async function runToolCall(
  panel: Panel,
  callId: string,
  name: string,
  args: Record<string, unknown>,
  exec?: unknown,
): Promise<void> {
  // The call's own details when it brought them, the turn's list otherwise.
  const tool = toolFromExec(name, exec) ?? state.tools.find((t) => t.name === name);
  state.toolsRunning += 1;
  let outcome: Awaited<ReturnType<typeof executeTool>>;
  try {
    outcome = tool
      ? await executeTool(tool, args, state.adapter)
      : ({ status: "error", result: { error: `Unknown tool: ${name}` }, verified: false } as const);
  } finally {
    state.toolsRunning -= 1;
  }

  /**
   * 🔴 RENDERED FROM THE RESULT, HERE, BEFORE IT IS EVEN SENT ON. This is the
   *    only moment the raw tool result exists in the browser, and it is ground
   *    truth: no model has touched it yet. Building the cards from the model's
   *    summary instead would put a hallucinated price in a box beside a real
   *    photograph.
   *
   *    Only on `ok`. An error or an empty result has nothing to show, and
   *    drawing a card from a failed call is the "200 that lied" wearing a
   *    picture. See docs/28 section 3.
   *
   *    Not for a re-check. Before running something the visitor approved, the server
   *    repeats the earlier lookup under an id ending ":freshN" to see whether the thing
   *    changed (backend boundary/freshness.ts). The visitor already saw those results once.
   */
  if (outcome.status === "ok" && !/:fresh\d+$/.test(callId)) {
    const cards = detectCards(outcome.result, window.location.href);
    if (cards) panel.addCards(cards);
  }

  await postToolResult(state.turnId, {
    site_key: SITE_KEY,
    call_id: callId,
    status: outcome.status,
    result: outcome.result,
    verified: outcome.verified,
    ...visitorProof(),
  });
}

/**
 * Sends a tool's result back, and tries again if the network drops it.
 *
 * 🔴 THE ACTION HAS ALREADY HAPPENED BY THE TIME THIS RUNS. One lost POST used
 *    to mean the server heard nothing, told the model the action failed, and
 *    the visitor asked again: a second invite, a second booking. So this tries
 *    for about 15 seconds, well inside the server's 30-second wait. It never
 *    re-runs the action, only re-sends what already happened, and the server
 *    ignores a copy of a result it already has. If every attempt fails the
 *    server says "not confirmed", never "failed". docs/28 section 7b.
 */
const RESULT_RETRY_MS = [0, 500, 1000, 2000, 4000, 8000];

async function postToolResult(turnId: string, body: Record<string, unknown>): Promise<void> {
  const json = JSON.stringify(body);
  for (const wait of RESULT_RETRY_MS) {
    if (wait) await new Promise((r) => setTimeout(r, wait));
    try {
      const res = await fetch(`${API_BASE}/v1/turn/${turnId}/tool-result`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: json,
        // Survives the page being closed or navigated away straight after the
        // action ran. Browsers cap a keepalive body at 64KB and reject a bigger
        // one outright, so a large result goes without it.
        keepalive: json.length < 60_000,
      });
      // Delivered, or refused for a reason another attempt cannot change: a
      // finished turn (404) or a malformed body (400).
      if (res.ok || (res.status >= 400 && res.status < 500 && res.status !== 429)) return;
    } catch {
      // Offline or dropped. Try again.
    }
  }
}

/**
 * 🔴 LEAVING THE PAGE WHILE AN ACTION RUNS ASKS FIRST. Closing the tab in the
 *    second a booking is being made cancels the request halfway, and nobody,
 *    us included, can then say whether it happened. Only while a tool is
 *    actually executing, which is a second or two: a "Leave site?" prompt at
 *    any other time would be ours to answer for on somebody else's website.
 */
window.addEventListener("beforeunload", (e) => {
  if (state.toolsRunning > 0) {
    e.preventDefault();
    // Safari still reads this rather than preventDefault.
    e.returnValue = "";
  }
});

/**
 * Fetches the answer to a turn whose stream dropped part way.
 *
 * 🔴 THE SERVER USUALLY HAS IT. The current model call finishes and the answer
 *    is saved whether or not anybody is still listening, so a drop mid-sentence
 *    is almost always recoverable within a few seconds of the network coming
 *    back. Polls for a minute, and only while the browser thinks it is online.
 *
 * 🔴 AND WHEN IT CANNOT, IT SAYS WHETHER AN ACTION MIGHT HAVE HAPPENED. A lost
 *    answer to a question is a nuisance; a lost answer to "cancel my order" is
 *    somebody who does not know whether their order is cancelled, and asking
 *    again blindly is how a duplicate happens.
 */
async function recoverTurn(panel: Panel, signal: AbortSignal): Promise<void> {
  const turnId = state.turnId;
  panel.endStream();

  if (turnId) {
    panel.showThinking("Reconnecting");
    const deadline = Date.now() + 60_000;
    let notFoundSince: number | undefined;
    while (Date.now() < deadline && !signal.aborted) {
      if (navigator.onLine) {
        try {
          const res = await fetch(`${API_BASE}/v1/turn/${turnId}/outcome`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ site_key: SITE_KEY, ...visitorProof() }),
            cache: "no-store",
          });
          // Not found yet: during a deploy the other process may still be
          // finishing and saving this turn. Given 15 seconds, then believed.
          if (res.status === 404) {
            notFoundSince ??= Date.now();
            if (Date.now() - notFoundSince > 15_000) break;
            await new Promise((r) => setTimeout(r, 2000));
            continue;
          }
          const o = (await res.json()) as Record<string, unknown>;
          if (o["status"] === "saved") {
            panel.settleAnswer(String(o["text"] ?? ""));
            if (o["conversation_id"]) state.conversationId = String(o["conversation_id"]);
            panel.commitTurn();
            panel.offerFeedback(String(o["message_id"] ?? ""));
            return;
          }
          if (o["status"] === "failed") break;
        } catch {
          // Still offline in practice. Keep waiting.
        }
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
  }

  if (signal.aborted) return;
  panel.hideThinking();
  panel.addNotice(
    state.turnRanTool
      ? "The connection dropped and the rest of that answer was lost. If you asked for an action, check whether it went through before asking again."
      : "The connection dropped and the rest of that answer was lost. Please ask again.",
    "error",
  );
}

/**
 * Sends a rating. Nothing on screen waits for it.
 *
 * 🔴 SWALLOWED ON PURPOSE, WHICH IS THE OPPOSITE OF postConfirm BELOW AND FOR
 *    THE OPPOSITE REASON. A confirmation that fails means a destructive action
 *    did not happen, and the person who pressed the button needs that fact. A
 *    rating that fails means we did not learn something. The visitor did us a
 *    courtesy; making them read an error about our storage spends their
 *    attention on our problem, and there is nothing for them to do about it.
 *
 * 🔴 IT CARRIES THE SAME anon_ref AND session_token A TURN DOES, because the
 *    server checks that whoever is rating this answer is the person the answer
 *    was written for. Without them the post is refused, correctly, and the
 *    thumb would silently do nothing.
 */
async function sendFeedback(
  messageId: string,
  rating: "up" | "down",
  comment?: string,
): Promise<void> {
  try {
    await fetch(`${API_BASE}/v1/feedback`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        site_key: SITE_KEY,
        message_id: messageId,
        rating,
        ...(comment ? { comment } : {}),
        ...(state.sessionToken ? { session_token: state.sessionToken } : {}),
        anon_ref: anonRef(),
      }),
    });
  } catch {
    // Offline, blocked, or the tab is closing. Nothing to say and nothing to do.
  }
}

/**
 * Sends the human's answer back to the waiting turn.
 *
 * 🔴 A FAILURE HERE USED TO BE SWALLOWED, and that is what turned a dropped
 *    connection into a panel stuck on "Running that…" forever. The body was
 *    `.catch(() => {})`, copied from postToolResult above, where swallowing IS
 *    right: a lost tool result times out server-side in thirty seconds and the
 *    model reports honestly, so there is genuinely nothing useful to say.
 *
 *    A confirmation is the opposite case. The server answers 404 "Unknown or
 *    finished turn" when the turn has already ended, and `{ ok: false }` when
 *    the turn is alive but nothing was waiting on that call. Both mean the same
 *    thing to the person who just pressed a button on a destructive action:
 *    IT DID NOT HAPPEN. That is the one fact they need and we already had it.
 *
 *    Never says "confirmed but we are not sure". The server does not run an
 *    action whose confirmation never arrived, so "nothing ran" is a fact, not a
 *    guess.
 */
async function postConfirm(
  panel: Panel,
  callId: string,
  decision: "allow" | "deny",
  values?: Record<string, unknown>,
): Promise<void> {
  const failed = () => {
    panel.expirePendingConfirm();
    panel.addNotice(
      "That confirmation did not reach us in time, so nothing ran. Ask again and I will redo it.",
      "error",
    );
    panel.hideThinking();
    panel.setBusy(false);
  };

  try {
    const res = await fetch(`${API_BASE}/v1/turn/${state.turnId}/confirm`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        site_key: SITE_KEY,
        call_id: callId,
        decision,
        ...(values ? { values } : {}),
        ...visitorProof(),
      }),
    });
    if (!res.ok) return failed();
    const body = (await res.json().catch(() => null)) as { ok?: boolean } | null;
    // `ok: false` means the turn is alive but nobody was waiting on this call,
    // which is the same dead end from the user's side.
    if (body && body.ok === false) failed();
  } catch {
    failed();
  }
}

/**
 * The reply to a collect-only card.
 *
 * Same failure handling as postConfirm and for the same reason: the server does
 * not run anything whose values never arrived, so "nothing ran" is a fact here
 * rather than a hopeful guess.
 */
async function postInput(
  panel: Panel,
  callId: string,
  values: Record<string, unknown> | null,
): Promise<void> {
  const failed = () => {
    panel.addNotice(
      "That did not reach us in time, so nothing ran. Ask again and I will redo it.",
      "error",
    );
    panel.hideThinking();
    panel.setBusy(false);
  };

  try {
    const res = await fetch(`${API_BASE}/v1/turn/${state.turnId}/input`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(
        values
          ? { site_key: SITE_KEY, call_id: callId, values, ...visitorProof() }
          : { site_key: SITE_KEY, call_id: callId, cancelled: true, ...visitorProof() },
      ),
    });
    if (!res.ok) return failed();
    const body = (await res.json().catch(() => null)) as { ok?: boolean } | null;
    if (body && body.ok === false) failed();
  } catch {
    failed();
  }
}

// A customer with a strict CSP is blocked SILENTLY, concludes the product is
// broken, and leaves without filing a ticket. The beacon turns a churn event
// into a support conversation.
try {
  boot();
} catch (err) {
  try {
    navigator.sendBeacon?.(
      `${API_BASE}/v1/boot-error`,
      JSON.stringify({
        site_key: SITE_KEY,
        message: err instanceof Error ? err.message : String(err),
        href: location.href,
      }),
    );
  } catch {
    /* nothing left to try */
  }
}
